import React, { useState } from "react";
import { TestID } from "../testids/testids";
import { COREButton } from "../../COREDesignSystem/Action/COREButton";
import { COREModal } from "../../COREDesignSystem/Overlay/COREModal";
import { COREBody } from "../../COREDesignSystem/Typography/COREBody";
import { useMutation } from "react-query";
import { AxiosResponse } from "axios";
import { appQueryClient } from "../state/appQueryClient";
import { keysToSnake } from "../global";
import { coreMessage } from "../../COREDesignSystem/Feedback/COREMessage";

export type UpgradeType =
  | "ACCU Method Specific Curve"
  | "Premium Insights"
  | "Market access"
  | "Trade Timeline"
  | "Markets Snapshot"
  | "Dashboards"
  | "Asset Intelligence"
  | "Asset Intelligence Portfolios"
  | "Chart Builder"
  | "Premium Notifications"
  | "Benchmarks";

// The upgrade type Asset Intelligence premium locks point at. Prefer this over
// repeating the literal; several older call sites still inline it.
export const PREMIUM_UPGRADE_TYPE: UpgradeType = "Asset Intelligence";

export type SubscriptionUpgradeProps = {
  testID: TestID;
  isOpen: boolean;
  closer: () => void;
  showHint?: boolean;
  upgradeType: UpgradeType;
};

const SubscriptionUpgradeMessage: React.FC<
  Pick<SubscriptionUpgradeProps, "upgradeType">
> = ({ upgradeType }) => {
  switch (upgradeType) {
    case "Premium Notifications":
      return `Contact our team to arrange an upgrade to your subscription that includes notifications.`;
    case "Premium Insights":
      return `Contact our team to arrange an upgrade to your subscription that includes premium reports.`;
    case "Market access":
      return `Contact our team to arrange an upgrade to your subscription that includes market access.`;
    case "ACCU Method Specific Curve":
      return `Contact our team to arrange an upgrade to your subscription that includes ACCU Method Specific curves premium content.`;
    case "Asset Intelligence Portfolios":
      return `Contact our team to arrange an upgrade to your subscription that includes asset intelligence portfolios.`;
    case "Chart Builder":
      return `Contact our team to arrange an upgrade to your subscription that includes broader market access`;
    default:
      return `Contact our team to arrange an upgrade to your subscription that includes premium access.`;
  }
};

// Separated RequestUpgradeButton component
type RequestUpgradeButtonProps = {
  upgradeType: UpgradeType;
  closer: () => void;
  icon?: React.ReactElement<any, any> | null | undefined;
};

export const RequestUpgradeButton: React.FC<RequestUpgradeButtonProps> = ({
  upgradeType,
  closer,
  icon,
}) => {
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const sourceURL = window.location.href;
  const { mutate: verifyMutation } = useMutation<AxiosResponse<unknown>>(
    () => {
      return appQueryClient.fetchQuery({
        queryKey: [
          "sendUserUpgradeRequest",
          {
            action: "sendUserUpgradeRequest",
            body: JSON.stringify(
              keysToSnake({
                upgradeType: upgradeType,
                sourceUrl: sourceURL,
              })
            ),
            enabled: true,
          },
        ],
      });
    },
    {
      onSuccess: () => {
        closer();
        setIsSaving(false);
        return coreMessage({
          type: "success",
          content: "Request sent successfully.",
        });
      },
      onError: () => {
        closer();
        setIsSaving(false);
        return coreMessage({
          type: "error",
          content: "Request failed to send.",
        });
      },
    }
  );
  return (
    <COREButton
      size="lg"
      type="primary"
      block={true}
      key={"request-upgrade-button"}
      onClick={() => {
        setIsSaving(true);
        verifyMutation();
      }}
      disabled={isSaving}
      icon={icon}
    >
      Request upgrade
    </COREButton>
  );
};

export const SubscriptionUpgradeModal: React.FC<SubscriptionUpgradeProps> = ({
  testID,
  isOpen,
  closer,
  showHint,
  upgradeType,
}) => {
  return (
    <COREModal
      destroyOnClose
      open={isOpen}
      testID={testID}
      onCancel={closer}
      type="basic"
      title="Upgrade subscription"
      footer={[
        <RequestUpgradeButton
          key={"upgrade-btn"}
          upgradeType={upgradeType}
          closer={closer}
        />,
      ]}
      width={384}
      footerFullWidth={true}
    >
      <COREBody type="p2" marginBottom={!!showHint}>
        <SubscriptionUpgradeMessage upgradeType={upgradeType} />
      </COREBody>
      {showHint && (
        <COREBody type="p4" marginBottom={false}>
          Please note: the ACCU Quarterly Forecast is not included with premium
          reports and requires Carbon Markets + ACCU Forecast subscription to
          access.
        </COREBody>
      )}
    </COREModal>
  );
};
